import * as ngCommon from '@angular/common'
import * as ngCore from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import {
  formatDistanceToNow,
  formatDuration,
  intervalToDuration,
} from 'date-fns'
import * as rxjs from 'rxjs'

import * as state from '../../../../../../../state'

/**
 * How often (in milliseconds) to check if the text displayed for the date
 * should change
 *
 * For example, the date may something like '1 minute ago' and it won't change
 * from that until we check again to see if it should now say '2 minutes ago'
 */
const DATE_UPDATE_FREQ = 10e3

@ngCore.Component({
  imports: [
    ngCommon.JsonPipe,
    ds.FormattedValue.Module,
    ds.tooltip.Directive,
    ds.overlay.attached.Component,
    ds.overlay.confirmation.Component,
  ],
  selector: 'atn-process',
  standalone: true,
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
/**
 * Component for displaying a single process in the process list slideout
 */
export class Component {
  /** @see {@link platform.ProcessEcService} */
  private readonly _processEc = ngCore.inject(state.relay.processes.Collection)
  /** @see {@link state.relay.processes.plugins.ui.Plugin} */
  public readonly processesUiPlugin = this._processEc.plugins
    .lookup(state.relay.processes.plugins.ui.Plugin)
    .get()

  /** @see {@link state.relay.processes.plugins.polling.Plugin} */
  public readonly processPollingPlugin = this._processEc.plugins
    .lookup(state.relay.processes.plugins.polling.Plugin)
    .get()

  /** @see {@link state.me.Feature} */
  private readonly _meFeature = ngCore.inject(state.me.Feature)

  /**
   * Exposes {@link ents.core.common.value.types.Intrinsic} to HTML template
   */
  public readonly Intrinsic = ents.core.common.value.types.Intrinsic

  /**
   * Exposes {@link ents.core.relay.processes.Execution.Status} to HTML template
   */
  public readonly ProcessStatus = ents.core.relay.processes.Execution.Status

  /** @see {@link ents.core.relay.processes.Process.WithDetails} */
  public readonly processSg =
    ngCore.input.required<ents.core.relay.processes.Process.WithDetails>({
      alias: 'process',
    })

  /** Human-readable representation of the date from {@link processSg} */
  public readonly processDateSg = ngCore.computed(
    (
      process = this.processSg(),
      // Causes this computed to update every so often. We don't actually need
      // the value. We just need to call the signal
      updater = this._processDateUpdaterSg(),
    ) => formatDistanceToNow(process.date, { addSuffix: true }),
  )

  /**
   * Human-readable representation of how long the process ran for.
   * Only returns a value when the process has completed.
   */
  public readonly processDurationSg = ngCore.computed(
    ({ duration, status } = this.processSg()) => {
      const isCompleted =
        status === ents.core.relay.processes.Execution.Status.Succeeded ||
        status === ents.core.relay.processes.Execution.Status.Failed

      if (!isCompleted || duration === undefined) {
        return undefined
      }

      return formatDuration(intervalToDuration({ end: duration, start: 0 }), {
        format: ['years', 'months', 'weeks', 'days', 'hours', 'minutes'],
      })
    },
  )

  /**
   * Returns the {@link ents.core.admin.App} that this process is associated
   * with, or undefined if it is not linked to any App
   */
  private readonly _processAppSg = ngCore.computed(
    ({ appId } = this.processSg()) =>
      appId
        ? this._meFeature.contexts.selectors
            .getApp({ app: { id: appId } })
            .asSignal()
        : undefined,
  )

  /**
   * Returns the {@link ents.core.admin.App} name that this process is
   * associated with, or undefined if it is not linked to any App
   */
  private readonly _processAppNameSg = ngCore.computed(
    (processApp = this._processAppSg()) => processApp?.name,
  )

  /**
   * Returns the {@link ents.core.admin.Scenario} that this process is associated
   * with, or undefined if it is not linked to any App
   */
  private readonly _processScenarioSg = ngCore.computed(
    ({ scenarioId } = this.processSg(), app = this._processAppSg()) =>
      // NOTE: Retrieve scenario data from the process's app data instead of the
      // Scenarios EC, as the Scenarios EC may not have cached data if no app is
      // selected.
      scenarioId
        ? app?.scenarios.find(scenario => scenario.id === scenarioId)
        : undefined,
  )

  /**
   * Returns the {@link ents.core.admin.Scenario} name that this process is
   * associated with, or undefined if it is not linked to any Scenario
   */
  private readonly _processScenarioNameSg = ngCore.computed(
    (processScenario = this._processScenarioSg()) => processScenario?.name,
  )

  /**
   * Returns a stringified representation of the process's associations.
   */
  public readonly processAssociationsSg = ngCore.computed(
    (
      appName = this._processAppNameSg(),
      scenarioName = this._processScenarioNameSg(),
    ) => {
      let associations: string | undefined

      if (appName) {
        associations = appName
        if (scenarioName) {
          associations += ` > ${scenarioName}`
        }
      }

      return associations
    },
  )

  /** Indicates whether the overlay for process options is opened */
  public isOptionsOverlayOpened = false

  /** Indicates whether the delete confirmation dialog is opened */
  public presentProcessDeletionDialogSg = ngCore.computed(() =>
    this._processEc.plugins.editingPlugin.feature.selectors
      .isMarkedForDeletion(this.processSg().id)
      .asSignal(),
  )

  /** Lookup used to get colors and icon based on process status */
  public statusLookup: Record<
    ents.core.relay.processes.Execution.Status,
    { fillColor: string; progressColor: string; iconClass: string }
  > = {
    [ents.core.relay.processes.Execution.Status.InProgress]: {
      fillColor: ds.color.Legacy.Primary.Lighter,
      iconClass: 'atn-waiting atn-blue atn-spin',
      // TODO: Check if Status color 'Information' fits for InProgress
      progressColor: ds.color.Legacy.Main.Primary,
    },
    [ents.core.relay.processes.Execution.Status.Succeeded]: {
      fillColor: ds.color.Legacy.Success.Lighter,
      iconClass: 'atn-done atn-with-circle',
      progressColor: ds.color.Legacy.Status.Success,
    },
    [ents.core.relay.processes.Execution.Status.Failed]: {
      fillColor: ds.color.Legacy.Danger.Light,
      iconClass: 'atn-error',
      progressColor: ds.color.Legacy.Status.Danger,
    },
    [ents.core.relay.processes.Execution.Status.Stopping]: {
      fillColor: ds.color.Legacy.Warning.Light,
      iconClass: 'atn-warning-triangle',
      progressColor: ds.color.Legacy.Status.Warning,
    },
  }

  /**
   * Whether this process was explicitly stopped by a user.
   *
   * TODO: Checking a magic string is not ideal. Consider adding a dedicated
   * status or flag on the backend so we don't rely on matching `detail` text.
   */
  private readonly _isStoppedByUserSg = ngCore.computed(
    ({ detail } = this.processSg()) => detail === 'Stopped by user',
  )

  /**
   * Returns the effective status properties to use for display.
   *
   * - Stopped by user → red (Failed) — even if some steps succeeded
   * - Partially failed (natural) → orange (Warning)
   * - Otherwise → use the process status directly
   */
  public readonly displayStatusSg = ngCore.computed(
    ({ isPartiallyFailed, status } = this.processSg()) => {
      if (isPartiallyFailed && !this._isStoppedByUserSg()) {
        return {
          fillColor: ds.color.Legacy.Warning.Light,
          iconClass: 'atn-warning-triangle',
          progressColor: ds.color.Legacy.Status.Warning,
        }
      }
      return this.statusLookup[status]
    },
  )

  /**
   * Just used to force {@link processDateSg} to update every
   * {@link DATE_UPDATE_FREQ} milliseconds
   */
  private readonly _processDateUpdaterSg = toSignal(
    rxjs.interval(DATE_UPDATE_FREQ),
  )

  /** Handling expand process.*/
  public expandButtonClickHandler(
    process: ents.core.relay.processes.Process,
  ): void {
    this.processesUiPlugin.feature.actions.toggleExpand
      .from('Expand.Process.Button.Clicked')
      .create({ processId: process.id })
      .dispatch()
  }

  /** Handler that starts deletion process */
  public onProcessDeleteOptionClickedHandler(): void {
    const processId = this.processSg().id
    this._processEc.plugins.editingPlugin.feature.actions.markForDeletion
      .from(`ProcessSlideout.Process-${processId}.Options.Delete.Clicked`)
      .dispatch({
        id: processId,
      })
  }

  /** Handler to submit delete */
  public onDeleteDialogYesBtnClickedHandler(): void {
    this._processEc.plugins.editingPlugin.feature.actions.submit
      .from(
        `ProcessSlideout.Process-${this.processSg().id}.DeleteDialog.Buttons.Yes.Clicked`,
      )
      .dispatch({
        delete: true,
      })
  }

  /** Handler to cancel delete */
  public onDeleteDialogNoBtnClickedHandler(): void {
    this._processEc.plugins.editingPlugin.feature.actions.discard
      .from(
        `ProcessSlideout.Process-${this.processSg().id}.DeleteDialog.Buttons.No.Clicked`,
      )
      .dispatch({
        delete: true,
      })
  }

  /**
   * Returns the effective icon class for a step, taking into account whether
   * the process was stopped by a user. Steps that were still in-progress or
   * hadn't started yet get a yellow warning icon instead of the spinner.
   */
  public getEffectiveStepIconClass(
    step: ents.core.relay.processes.Process.Step,
  ): string {
    if (this._isStoppedByUserSg()) {
      if (
        !step.status ||
        step.status === ents.core.relay.processes.Execution.Status.InProgress
      ) {
        return this.statusLookup[
          ents.core.relay.processes.Execution.Status.Stopping
        ].iconClass
      }
    }
    if (!step.status) {
      return 'atn-icon atn-repeat atn-dark default'
    }
    return this.statusLookup[step.status].iconClass
  }

  /**
   * Returns the tooltip text for a step, showing "Stopped before finished"
   * for steps that were interrupted by a user-initiated stop.
   */
  public getEffectiveStepTooltip(
    step: ents.core.relay.processes.Process.Step,
  ): string | undefined {
    if (this._isStoppedByUserSg()) {
      if (
        !step.status ||
        step.status === ents.core.relay.processes.Execution.Status.InProgress
      ) {
        return 'Stopped before finished'
      }
    }
    return step.detail
  }

  /** Handler that opens the stop confirmation dialog */
  public onProcessStopOptionClickedHandler(): void {
    const processId = this.processSg().id
    this.processesUiPlugin.feature.actions.markForStop
      .from(`ProcessSlideout.Process-${processId}.Options.Stop.Clicked`)
      .create({ processId })
      .dispatch()
  }

  /** Handler to confirm stop */
  public onStopConfirmedHandler(): void {
    const processId = this.processSg().id
    this.processesUiPlugin.feature.actions.stopProcess
      .from(
        `ProcessSlideout.Process-${processId}.StopDialog.Buttons.Yes.Clicked`,
      )
      .dispatch({ processId })
  }

  /** Handler to cancel stop */
  public onStopCancelledHandler(): void {
    this.processesUiPlugin.feature.actions.cancelStop
      .from(
        `ProcessSlideout.Process-${this.processSg().id}.StopDialog.Buttons.No.Clicked`,
      )
      .dispatch()
  }
}
